import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTasksThunk,
  createTaskThunk,
  updateTaskThunk,
  deleteTaskThunk,
} from '../thunks/crmTasksThunks';

const initialState = {
  items: [],
  loading: false,
  error: null,

  filters: {
    search: '',
    status: 'all',
    due: '',
    sortBy: 'manual',
  },

  viewMode: 'list',
  selectedTaskId: null,
  selectedTask: null,
  showDetailPanel: false,
  collapsedGroups: {},

  saving: {},
  deleting: {},
};

const crmTasksSlice = createSlice({
  name: 'crmTasks',
  initialState,
  reducers: {
    setTasks: (state, action) => {
      state.items = action.payload;
    },
    addTask: (state, action) => {
      state.items.push(action.payload);
    },
    updateTaskInList: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.items.findIndex(task => task._id === id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...changes };
      }
    },
    removeTask: (state, action) => {
      state.items = state.items.filter(task => task._id !== action.payload);
    },
    setSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
    },
    setDueFilter: (state, action) => {
      state.filters.due = action.payload;
    },
    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setSelectedTaskId: (state, action) => {
      state.selectedTaskId = action.payload;
    },
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload;
    },
    clearSelectedTask: (state) => {
      state.selectedTaskId = null;
      state.selectedTask = null;
    },
    setShowDetailPanel: (state, action) => {
      state.showDetailPanel = action.payload;
    },
    toggleCollapsedGroup: (state, action) => {
      const groupKey = action.payload;
      state.collapsedGroups[groupKey] = !state.collapsedGroups[groupKey];
    },
    optimisticUpdateTask: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.items.findIndex(task => task._id === id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...changes };
      }
      if (state.selectedTask?._id === id) {
        state.selectedTask = { ...state.selectedTask, ...changes };
      }
    },
    rollbackTask: (state, action) => {
      const { id, previousTask } = action.payload;
      const index = state.items.findIndex(task => task._id === id);
      if (index !== -1) {
        state.items[index] = previousTask;
      }
      if (state.selectedTask?._id === id) {
        state.selectedTask = previousTask;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTasksThunk
      .addCase(fetchTasksThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasksThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchTasksThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // createTaskThunk
      .addCase(createTaskThunk.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })

      // updateTaskThunk
      .addCase(updateTaskThunk.pending, (state, action) => {
        const id = action.meta.arg.id || action.meta.arg._id;
        if (id) {
          state.saving[id] = true;
        }
      })
      .addCase(updateTaskThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex(task => task._id === updated._id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        if (updated._id) {
          state.saving[updated._id] = false;
        }
      })
      .addCase(updateTaskThunk.rejected, (state, action) => {
        const id = action.meta.arg.id || action.meta.arg._id;
        if (id) {
          state.saving[id] = false;
        }
      })

      // deleteTaskThunk
      .addCase(deleteTaskThunk.pending, (state, action) => {
        const id = action.meta.arg;
        if (id) {
          state.deleting[id] = true;
        }
      })
      .addCase(deleteTaskThunk.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.items = state.items.filter(task => task._id !== id);
        if (id) {
          state.deleting[id] = false;
        }
      })
      .addCase(deleteTaskThunk.rejected, (state, action) => {
        const id = action.meta.arg;
        if (id) {
          state.deleting[id] = false;
        }
      });
  },
});

export const {
  setTasks,
  addTask,
  updateTaskInList,
  removeTask,
  setSearch,
  setStatusFilter,
  setDueFilter,
  setSortBy,
  resetFilters,
  setViewMode,
  setSelectedTaskId,
  setSelectedTask,
  clearSelectedTask,
  setShowDetailPanel,
  toggleCollapsedGroup,
  optimisticUpdateTask,
  rollbackTask,
} = crmTasksSlice.actions;

export default crmTasksSlice.reducer;
